import { Link } from 'react-router-dom';
import { useState } from 'react';
import SEO from '../../components/seo/SEO';
import SchemaMarkup from '../../components/seo/SchemaMarkup';
import { useLanguageLink } from '../../hooks/useLanguageLink';
import { ScoreGauge, MetricBar } from '../../components/article/DataViz';

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <div className="font-mono text-xs uppercase tracking-[0.22em] text-stone-500 mb-4">
      {children}
    </div>
  );
}

export default function BlogTrafficQualityScorePageRU() {
  const { createLink } = useLanguageLink();
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const faqs = [
    {
      question: 'Чем оценка качества трафика отличается от простой блокировки ботов?',
      answer: 'Блокировка ботов — это бинарное решение: пропустить или отклонить. Оценка качества трафика — это шкала. Она отвечает не на вопрос «бот или человек?», а на вопрос «насколько вероятно, что этот посетитель принесёт конверсию?». Реальный человек с residential-прокси из другой страны, зашедший на 2 секунды, — не бот, но и не качественный трафик. Score позволяет ранжировать источники, а не только отсекать явных ботов.',
    },
    {
      question: 'Можно ли оценивать качество трафика, не замедляя лендинг?',
      answer: 'Да. Axetra принимает решение на стороне сервера менее чем за 10 миллисекунд — до того, как страница начинает рендериться. Посетитель не замечает задержки, а вы получаете score по каждому визиту в реальном времени. Скорость загрузки лендинга не меняется.',
    },
    {
      question: 'Какие данные нужны, чтобы начать оценивать качество трафика?',
      answer: 'Ничего сверх обычной интеграции. Axetra анализирует сигналы, которые и так присутствуют в каждом HTTP-запросе и браузерной сессии: сеть, устройство, поведение, вовлечённость. Не нужны пиксели конверсий, CRM-выгрузки или ручная разметка — score считается с первого визита, а точность растёт по мере накопления данных вашей вертикали.',
    },
  ];

  const publishDate = '2026-07-28';

  const signals = [
    { idx: '01', name: 'Сеть и происхождение', tags: 'репутация IP · ASN · VPN/прокси · гео', desc: 'Откуда на самом деле пришёл запрос — residential, мобильная сеть или дата-центр — и совпадают ли IP, язык и таргетинг.' },
    { idx: '02', name: 'Устройство и браузер', tags: 'fingerprint · headless · автоматизация', desc: 'Реальное устройство за запросом — вскрывает headless-браузеры и инструменты автоматизации, несмотря на подделанные заголовки.' },
    { idx: '03', name: 'Поведение и тайминг', tags: 'мышь · скролл · ритм кликов', desc: 'Движение, скролл и микровариации тайминга, которые отличают живого человека от заранее записанного сценария.' },
    { idx: '04', name: 'Вовлечённость', tags: 'глубина · время · повторные визиты', desc: 'Глубина просмотра, время на странице и повторные визиты — признаки реального интереса, а не случайного захода.' },
  ];

  return (
    <>
      <SEO
        title="Как оценить качество рекламного трафика: гид по Traffic Quality Score (2026)"
        description="Клики — не то же самое, что клиенты. Разбираем, из каких сигналов складывается качество трафика, как построить систему оценки и как отсекать источники, которые сливают бюджет."
        canonical="/blog/traffic-quality-score"
        keywords="оценка качества трафика, анализ качества трафика, качество рекламного трафика, traffic quality score, фильтрация трафика, проверка трафика на ботов, невалидный трафик, антифрод для рекламы"
        ogImage="/images/og-blog-ml-bot-detection.jpg"
        ogType="article"
        publishedTime={publishDate}
        modifiedTime={publishDate}
      />
      <SchemaMarkup
        type="article"
        headline="Как оценить качество рекламного трафика: гид по Traffic Quality Score"
        author="Команда Axetra"
        datePublished={publishDate}
        dateModified={publishDate}
        image="/images/og-blog-ml-bot-detection.jpg"
      />
      <SchemaMarkup type="faq" questions={faqs} />

      <article className="bg-[#faf8f4] text-stone-900">

        {/* Masthead */}
        <div className="border-b border-stone-300">
          <div className="container-marketing max-w-5xl py-4 flex items-center justify-between font-mono text-xs uppercase tracking-[0.22em] text-stone-500">
            <span>Отчёт о качестве трафика</span>
            <span>Axetra · 2026</span>
          </div>
        </div>

        {/* Hero */}
        <header className="border-b border-stone-300">
          <div className="container-marketing max-w-5xl py-16 md:py-20">
            <div className="grid md:grid-cols-[1fr_auto] gap-12 md:gap-16 items-center">
              <div>
                <Eyebrow>Руководство · 11 мин · 28 июля 2026</Eyebrow>
                <h1 className="font-display font-bold text-4xl md:text-6xl leading-[1.05] tracking-tight mb-6">
                  Как оценить качество рекламного трафика
                </h1>
                <p className="text-lg md:text-xl text-stone-600 leading-relaxed max-w-xl">
                  Высокий CTR и тысячи кликов ничего не значат, если посетители не конвертят. Вот из чего складывается качество, как измерить его по источникам и как перестать платить за клики, которые не окупаются.
                </p>
              </div>
              <div className="justify-self-center md:justify-self-end">
                <ScoreGauge score={82} size={220} />
              </div>
            </div>
          </div>
        </header>

        {/* Thesis line */}
        <div className="border-y border-stone-800 bg-stone-900 text-[#faf8f4]">
          <div className="container-marketing max-w-5xl py-14 md:py-20">
            <div className="flex items-center gap-4 mb-8">
              <span className="font-mono text-xs uppercase tracking-[0.22em] text-stone-500">Тезис</span>
              <span className="h-px flex-1 bg-stone-700" />
            </div>
            <p className="font-display font-semibold text-4xl md:text-6xl leading-[1.08] tracking-tight max-w-3xl">
              Клики врут.<br />
              <span className="text-emerald-400">Сигналы говорят правду.</span>
            </p>
          </div>
        </div>

        <div className="container-marketing max-w-[680px] py-16 space-y-20">

          {/* 01 */}
          <section>
            <Eyebrow>01 / Почему клики врут</Eyebrow>
            <p className="text-lg text-stone-700 leading-relaxed mb-6">
              Две кампании. Обе привели по <span className="font-mono font-semibold text-stone-900">10 000</span> кликов за месяц, обе показали CTR около <span className="font-mono font-semibold text-stone-900">4%</span>, обе уложились в один бюджет. По дашборду — близнецы. Но первая принесла <span className="font-mono font-semibold text-emerald-700">180</span> заявок, а вторая — <span className="font-mono font-semibold text-red-700">11</span>. Разница не в объявлениях и не в ставках. Разница в качестве трафика.
            </p>
            <p className="text-lg text-stone-700 leading-relaxed mb-6">
              Клик — самая дешёвая и самая обманчивая метрика в перформансе. Его легко получить, легко накрутить и почти невозможно интерпретировать в отрыве от контекста. Бот кликает как человек. Случайный пользователь, промахнувшийся по объявлению, — как тёплый лид. В отчёте они неотличимы.
            </p>
            <p className="text-lg text-stone-700 leading-relaxed">
              Оценка качества решает это: вместо того чтобы считать клики, вы оцениваете, <em>кто</em> за ними стоит и насколько вероятно, что визит превратится в деньги.
            </p>

            <blockquote className="my-10 border-l-2 border-emerald-600 pl-6">
              <p className="font-display text-2xl md:text-3xl leading-snug text-stone-900">
                Если метрику можно улучшить, добавив ботов, — это метрика тщеславия.
              </p>
            </blockquote>

            <p className="text-lg text-stone-700 leading-relaxed">
              Клики, показы и CTR боты накручивают легко. Качество трафика — нет: боты не конвертят и не ведут себя как настоящие покупатели под нагрузкой из сотен сигналов.
            </p>
          </section>

          {/* 02 */}
          <section>
            <Eyebrow>02 / Из чего складывается оценка</Eyebrow>
            <h2 className="font-display font-bold text-3xl md:text-4xl mb-6">Четыре независимые группы сигналов</h2>
            <p className="text-lg text-stone-700 leading-relaxed mb-8">
              Ни один сигнал не приговор. Посетитель за VPN — не обязательно бот; короткая сессия — не всегда мусор. Но подделать сеть, устройство, поведение и вовлечённость <em>одновременно</em>, так чтобы они сошлись в естественный человеческий профиль, экспоненциально сложнее, чем подделать один. Именно эту совокупность и оценивает score.
            </p>

            <div className="border-t border-stone-300">
              {signals.map((s) => (
                <div key={s.idx} className="grid grid-cols-[auto_1fr] gap-5 py-6 border-b border-stone-300">
                  <div className="font-mono text-sm text-emerald-700 pt-1">{s.idx}</div>
                  <div>
                    <h3 className="font-display font-semibold text-xl mb-1">{s.name}</h3>
                    <p className="text-stone-600 leading-relaxed mb-2">{s.desc}</p>
                    <p className="font-mono text-xs uppercase tracking-wider text-stone-400">{s.tags}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* 03 */}
          <section>
            <Eyebrow>03 / Scorecard</Eyebrow>
            <h2 className="font-display font-bold text-3xl md:text-4xl mb-6">Как читать сигналы</h2>
            <p className="text-lg text-stone-700 leading-relaxed mb-8">
              Рабочая шпаргалка. Ни один красный флаг сам по себе не приговор — но если источник собирает их пачками, его пора отключать.
            </p>

            <div className="overflow-x-auto border-t-2 border-stone-900">
              <table className="w-full text-sm">
                <thead>
                  <tr className="font-mono text-xs uppercase tracking-wider">
                    <th className="text-left py-3 pr-4 text-stone-500 font-medium">Сигнал</th>
                    <th className="text-left py-3 px-4 text-emerald-700 font-medium">Качество</th>
                    <th className="text-left py-3 pl-4 text-red-700 font-medium">Красный флаг</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Сеть', 'Residential / мобильный IP', 'Дата-центр, публичный VPN'],
                    ['География', 'IP · язык · таргетинг совпадают', 'IP и язык не совпадают'],
                    ['Устройство', 'Реальный браузер и устройство', 'Headless / автоматизация'],
                    ['Тайминг', 'Естественная вариативность', '«Слишком идеальная» равномерность'],
                    ['Глубина', 'Скролл, переходы, время', 'Отказ за 1–2 сек'],
                    ['Частота', 'Уникальные разные сессии', 'Всплески одинаковых сессий'],
                    ['Конверсия', 'Стабильно даёт заявки', 'Много кликов, ноль действий'],
                  ].map(([sig, good, bad]) => (
                    <tr key={sig} className="border-b border-stone-200 align-top">
                      <td className="py-3 pr-4 font-mono text-stone-900">{sig}</td>
                      <td className="py-3 px-4 text-stone-700">{good}</td>
                      <td className="py-3 pl-4 text-stone-700">{bad}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        {/* 04 — case study centerpiece */}
        <section className="border-y border-stone-300 bg-white">
          <div className="container-marketing max-w-5xl py-16">
            <div className="max-w-[680px] mx-auto md:mx-0">
              <Eyebrow>04 / Кейс</Eyebrow>
              <h2 className="font-display font-bold text-3xl md:text-4xl mb-6">Одинаковый CTR, разный результат</h2>
              <p className="text-lg text-stone-700 leading-relaxed mb-12">
                Вернёмся к двум кампаниям. По верхнеуровневым метрикам они идентичны. Разложите трафик по сигналам — и разница видна сразу.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Кампания A */}
              <div className="border border-stone-300 p-8 bg-[#faf8f4]">
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <div className="font-mono text-xs uppercase tracking-[0.18em] text-stone-500">Кампания A</div>
                    <div className="font-display text-2xl font-bold text-emerald-700">180 заявок</div>
                  </div>
                  <ScoreGauge score={82} size={132} />
                </div>
                <div className="space-y-4">
                  <MetricBar label="Residential / мобильные" value={88} display="88%" tone="good" />
                  <MetricBar label="Глубина визита" value={68} display="3.4 экрана" tone="good" />
                  <MetricBar label="Сессии короче 2 сек" value={6} display="6%" tone="good" />
                  <MetricBar label="Совпадение гео" value={96} display="96%" tone="good" />
                </div>
              </div>

              {/* Кампания B */}
              <div className="border border-stone-300 p-8 bg-[#faf8f4]">
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <div className="font-mono text-xs uppercase tracking-[0.18em] text-stone-500">Кампания B</div>
                    <div className="font-display text-2xl font-bold text-red-700">11 заявок</div>
                  </div>
                  <ScoreGauge score={28} size={132} />
                </div>
                <div className="space-y-4">
                  <MetricBar label="Residential / мобильные" value={39} display="39%" tone="bad" />
                  <MetricBar label="Глубина визита" value={22} display="1.1 экрана" tone="bad" />
                  <MetricBar label="Сессии короче 2 сек" value={74} display="74%" tone="bad" />
                  <MetricBar label="Совпадение гео" value={39} display="39%" tone="bad" />
                </div>
              </div>
            </div>

            <p className="text-lg text-stone-700 leading-relaxed mt-12 max-w-[680px] mx-auto md:mx-0">
              Одинаковый CTR скрывал всё, что имеет значение. Кампания B платила за клики ботов и мотивированный трафик с одной площадки. Отключив её и перераспределив бюджет на источники со score выше <span className="font-mono font-semibold">60</span>, рекламодатель поднял число заявок при том же расходе — не меняя ни объявлений, ни ставок.
            </p>
          </div>
        </section>

        <div className="container-marketing max-w-[680px] py-16 space-y-20">

          {/* 05 */}
          <section>
            <Eyebrow>05 / Playbook</Eyebrow>
            <h2 className="font-display font-bold text-3xl md:text-4xl mb-8">Как поднять качество трафика</h2>
            <div className="border-t border-stone-300">
              {[
                { n: '1', t: 'Оценивайте визит, а не кампанию', d: 'Средний score по кампании прячет проблему. Разбивайте качество по источникам, площадкам и объявлениям — плохой трафик концентрируется в нескольких местах.' },
                { n: '2', t: 'Отсекайте источники с низким score', d: 'Площадки, которые стабильно набирают низкий score, исключайте. Это не блокировка людей — это отказ платить за клики, которые не конвертят.' },
                { n: '3', t: 'Фильтруйте дата-центры, прокси, VPN', d: 'Трафик из дата-центров почти не превращается в клиентов. Его исключение — самый быстрый способ поднять средний score без потери реальной аудитории.' },
                { n: '4', t: 'Следите за вовлечённостью', d: 'Глубина визита и время на странице предсказывают конверсию лучше, чем CTR. Смещайте ставки к источникам, где люди реально взаимодействуют.' },
                { n: '5', t: 'Дайте системе выучить вашу вертикаль', d: 'Профиль «нормального» посетителя у e-commerce, лидогенерации и B2B разный. Чем дольше работает оценка, тем точнее она отделяет качество в вашей нише.' },
              ].map((s) => (
                <div key={s.n} className="grid grid-cols-[auto_1fr] gap-5 py-6 border-b border-stone-300">
                  <div className="font-mono text-lg text-emerald-700">{s.n}</div>
                  <div>
                    <h3 className="font-display font-semibold text-xl mb-1">{s.t}</h3>
                    <p className="text-stone-600 leading-relaxed">{s.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* 06 */}
          <section>
            <Eyebrow>06 / Различие</Eyebrow>
            <h2 className="font-display font-bold text-3xl md:text-4xl mb-8">Метрики тщеславия против метрик качества</h2>
            <div className="overflow-x-auto border-t-2 border-stone-900">
              <table className="w-full text-sm">
                <thead>
                  <tr className="font-mono text-xs uppercase tracking-wider">
                    <th className="text-left py-3 pr-4 text-stone-500 font-medium">Вопрос</th>
                    <th className="text-left py-3 px-4 text-stone-400 font-medium">Тщеславие</th>
                    <th className="text-left py-3 pl-4 text-emerald-700 font-medium">Качество</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Сколько людей пришло?', 'Показы, клики', 'Доля валидных визитов'],
                    ['Насколько кликабельно?', 'CTR', 'CTR среди качества'],
                    ['Что на странице?', '—', 'Глубина и время'],
                    ['Где реальная ценность?', 'Клики по источникам', 'Score по источникам'],
                    ['Накрутить ботами?', 'Да, легко', 'Нет'],
                  ].map(([q, v, ql]) => (
                    <tr key={q} className="border-b border-stone-200 align-top">
                      <td className="py-3 pr-4 font-mono text-stone-900">{q}</td>
                      <td className="py-3 px-4 text-stone-500">{v}</td>
                      <td className="py-3 pl-4 text-stone-800 font-medium">{ql}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        {/* Stat band */}
        <section className="border-y border-stone-300 bg-stone-900 text-[#faf8f4]">
          <div className="container-marketing max-w-5xl py-14">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-6">
              {[
                { stat: '<10ms', label: 'Score по визиту', sub: 'Без замедления лендинга' },
                { stat: '900+', label: 'Сигналов на визит', sub: 'Сеть · устройство · поведение' },
                { stat: '0–100', label: 'Единая шкала', sub: 'Сравнивайте любые источники' },
              ].map((s) => (
                <div key={s.stat} className="text-center md:border-r md:border-stone-700 md:last:border-0">
                  <div className="font-mono font-bold text-5xl mb-2 tabular-nums text-emerald-400">{s.stat}</div>
                  <div className="font-display text-lg">{s.label}</div>
                  <div className="text-sm text-stone-400 mt-1">{s.sub}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <div className="container-marketing max-w-[680px] py-16 space-y-20">

          {/* CTA */}
          <section>
            <div className="border border-stone-300 bg-white p-10 text-center">
              <h3 className="font-display font-bold text-2xl md:text-3xl mb-3">Узнайте реальное качество вашего трафика</h3>
              <p className="text-stone-600 mb-8 max-w-md mx-auto">
                Axetra присваивает Traffic Quality Score каждому визиту и показывает, какие источники приводят живых людей. Настройка за 5 минут, 14 дней бесплатно.
              </p>
              <Link
                to={createLink('register')}
                className="inline-block bg-emerald-600 text-white font-semibold px-8 py-4 rounded-md hover:bg-emerald-700 transition-colors"
              >
                Начать бесплатно
              </Link>
            </div>
          </section>

          {/* FAQ */}
          <section>
            <Eyebrow>FAQ</Eyebrow>
            <h2 className="font-display font-bold text-3xl md:text-4xl mb-8">Частые вопросы</h2>
            <div className="border-t border-stone-300">
              {faqs.map((faq, index) => (
                <div key={index} className="border-b border-stone-300">
                  <button
                    onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                    className="w-full flex items-center justify-between py-5 text-left"
                  >
                    <span className="font-display font-semibold text-lg text-stone-900 pr-4">{faq.question}</span>
                    <svg className={`w-5 h-5 text-stone-500 flex-shrink-0 transition-transform ${openFaqIndex === index ? 'rotate-45' : ''}`}
                      fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                  {openFaqIndex === index && (
                    <p className="pb-6 text-stone-600 leading-relaxed max-w-[600px]">{faq.answer}</p>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Conclusion */}
          <section>
            <Eyebrow>Заключение</Eyebrow>
            <p className="text-lg text-stone-700 leading-relaxed mb-6">
              Клики говорят, что кто-то нажал на объявление. Качество трафика говорит, стоило ли это ваших денег. Разница между растущей и убыточной кампанией почти никогда не видна в CTR — она прячется в сигналах, которых обычный рекламный кабинет не показывает.
            </p>
            <p className="text-lg text-stone-700 leading-relaxed">
              Начните оценивать трафик по источникам, отключайте то, что стабильно проваливает score, и перераспределяйте бюджет туда, где конвертят живые люди. Это не требует новых объявлений или большего бюджета — только честного взгляда на то, за что вы платите.
            </p>
          </section>
        </div>
      </article>
    </>
  );
}
